import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import {
  formatCost,
  formatPricePerMillion,
  formatTokens,
  formatTokensCompact,
} from '../../constants/aiUsage';

/**
 * Cost incurred per AI model.
 *
 * Broken out by model because the default model is a tenant setting but users
 * may switch model per card at runtime; each run is billed at the rate of the
 * model that actually ran. "Billable" columns show only the tokens beyond the
 * client's allowance — inside the allowance, cost is zero by design.
 *
 * Scrolls horizontally inside its own container so the page body never does.
 *
 * @param {object} props
 * @param {object[]} [props.rows] per-model usage rows from GET /ai/usage
 * @param {string} [props.currency] ISO currency code for the money columns
 * @param {boolean} [props.compact] denser layout, for the header popover
 * @param {string} [props.emptyMessage] shown when there is no usage yet
 */
const AiCostGrid = ({
  rows = [],
  currency = 'USD',
  compact = false,
  emptyMessage = 'No AI usage recorded yet.',
}) => {
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({
          inputTokens: acc.inputTokens + (row.inputTokens || 0),
          outputTokens: acc.outputTokens + (row.outputTokens || 0),
          billableInputTokens: acc.billableInputTokens + (row.billableInputTokens || 0),
          billableOutputTokens: acc.billableOutputTokens + (row.billableOutputTokens || 0),
          inputCost: acc.inputCost + (row.inputCost || 0),
          outputCost: acc.outputCost + (row.outputCost || 0),
          totalCost: acc.totalCost + (row.totalCost || 0),
        }),
        {
          inputTokens: 0,
          outputTokens: 0,
          billableInputTokens: 0,
          billableOutputTokens: 0,
          inputCost: 0,
          outputCost: 0,
          totalCost: 0,
        },
      ),
    [rows],
  );

  if (!rows.length) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  const cellSx = { fontSize: compact ? '0.75rem' : '0.875rem', whiteSpace: 'nowrap' };
  const headSx = { ...cellSx, fontWeight: 600 };

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderRadius: '8px', overflowX: 'auto', maxWidth: '100%' }}
    >
      <Table size="small" sx={{ minWidth: compact ? 520 : 720 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={headSx}>Model</TableCell>
            <TableCell sx={headSx} align="right">
              Rate (in / out)
            </TableCell>
            <TableCell sx={headSx} align="right">
              Input tokens
            </TableCell>
            <TableCell sx={headSx} align="right">
              Output tokens
            </TableCell>
            <TableCell sx={headSx} align="right">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                Billable
                <Tooltip
                  arrow
                  title="Tokens beyond the client's allowance. Tokens inside the allowance are not charged."
                >
                  <InfoIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                </Tooltip>
              </Box>
            </TableCell>
            <TableCell sx={headSx} align="right">
              Input cost
            </TableCell>
            <TableCell sx={headSx} align="right">
              Output cost
            </TableCell>
            <TableCell sx={headSx} align="right">
              Total cost
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row) => {
            const rowCurrency = row.currency || currency;
            const billable = (row.billableInputTokens || 0) + (row.billableOutputTokens || 0);

            return (
              <TableRow key={row.modelCode || row.aiModelId} hover>
                <TableCell sx={cellSx}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 'inherit' }}>
                    {row.modelName || row.modelCode}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {row.provider || row.modelCode}
                  </Typography>
                  {row.isActive === false && (
                    <Chip label="Retired" size="small" sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }} />
                  )}
                </TableCell>

                <TableCell sx={cellSx} align="right">
                  <Tooltip
                    arrow
                    title="Current list price. Recorded usage keeps the price it was billed at."
                  >
                    <span>
                      {formatPricePerMillion(row.currentInputPricePerMillion, rowCurrency)}
                      {' / '}
                      {formatPricePerMillion(row.currentOutputPricePerMillion, rowCurrency)}
                    </span>
                  </Tooltip>
                </TableCell>

                <TableCell sx={cellSx} align="right">
                  <Tooltip arrow title={formatTokens(row.inputTokens)}>
                    <span>{formatTokensCompact(row.inputTokens)}</span>
                  </Tooltip>
                </TableCell>

                <TableCell sx={cellSx} align="right">
                  <Tooltip arrow title={formatTokens(row.outputTokens)}>
                    <span>{formatTokensCompact(row.outputTokens)}</span>
                  </Tooltip>
                </TableCell>

                <TableCell sx={cellSx} align="right">
                  {billable > 0 ? (
                    <Tooltip
                      arrow
                      title={`${formatTokens(row.billableInputTokens)} input + ${formatTokens(
                        row.billableOutputTokens,
                      )} output`}
                    >
                      <span>{formatTokensCompact(billable)}</span>
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Within limit
                    </Typography>
                  )}
                </TableCell>

                <TableCell sx={cellSx} align="right">
                  {formatCost(row.inputCost, rowCurrency)}
                </TableCell>
                <TableCell sx={cellSx} align="right">
                  {formatCost(row.outputCost, rowCurrency)}
                </TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 600 }} align="right">
                  {formatCost(row.totalCost, rowCurrency)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>

        <TableFooter>
          <TableRow sx={{ backgroundColor: '#fafafa' }}>
            <TableCell sx={{ ...headSx, color: 'text.primary' }} colSpan={2}>
              Total
            </TableCell>
            <TableCell sx={headSx} align="right">
              <Tooltip arrow title={formatTokens(totals.inputTokens)}>
                <span>{formatTokensCompact(totals.inputTokens)}</span>
              </Tooltip>
            </TableCell>
            <TableCell sx={headSx} align="right">
              <Tooltip arrow title={formatTokens(totals.outputTokens)}>
                <span>{formatTokensCompact(totals.outputTokens)}</span>
              </Tooltip>
            </TableCell>
            <TableCell sx={headSx} align="right">
              {formatTokensCompact(totals.billableInputTokens + totals.billableOutputTokens)}
            </TableCell>
            <TableCell sx={headSx} align="right">
              {formatCost(totals.inputCost, currency)}
            </TableCell>
            <TableCell sx={headSx} align="right">
              {formatCost(totals.outputCost, currency)}
            </TableCell>
            <TableCell sx={headSx} align="right">
              {formatCost(totals.totalCost, currency)}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default AiCostGrid;
